<template>
  <div id="area-chooser" class="page container">
    <div class="row">
      <div class="col-xs-12 col-md-10 col-md-offset-1 col-lg-8 col-lg-offset-2">
        <div class="welcome card card-borderless">
          <div class="user-details row">
            <h2>{{ user.name }}</h2>
            <h3>{{ currentProgress.level.name }}</h3>
          </div>
          <div class="user-score row">
            <div class="score-column col-xs-4">
              <div class="number">
                {{ completeCategoriesCount
                }}<slash style="margin: 0 4px" /><span class="all-count">{{
                  allCategoriesCount
                }}</span>
              </div>
              <div class="description svg-line">
                <check-badge class="svg-icon" />
                <span v-html="$t('AREA_CHOOSER_COMPLETE_COURSES')" />
              </div>
            </div>
            <div class="vertical-divider" style="height: 61px" />
            <div class="score-column col-xs-4">
              <div class="number">
                {{ allAreasAchievementsCount }}
              </div>
              <div class="description svg-line">
                <trophy class="svg-icon" />
                {{ $t("AREA_CHOOSER_ACHIEVEMENTS_COUNT") }}
              </div>
            </div>
            <div class="vertical-divider" style="height: 61px" />
            <div class="score-column col-xs-4">
              <div class="number">
                {{ allAreasScore }}
              </div>
              <div class="description svg-line">
                <diamond class="svg-icon" />
                {{ $t("AREA_CHOOSER_TOTAL_SCORE") }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="areas-list row">
      <h3 class="col-xs-12 col-md-10 col-md-offset-1 col-lg-8 col-lg-offset-2">
          {{ $t("AREA_CHOOSER_LIST") }}
      </h3>

      <div class="col-xs-12 col-md-10 col-md-offset-1 col-lg-8 col-lg-offset-2">
        <div
          class="card"
          v-for="areaProgress in allAreasProgressSorted"
          :key="areaProgress.id"
          style="margin-bottom: 20px"
        >
          <div class="row area-specific-part">
            <div class="col-xs-8 area-chooser-details">
              <div class="area-name-row">
                <h2>{{ area(areaProgress.id).name }}</h2>
              </div>

              <div class="area-description-row">
                {{ area(areaProgress.id).description }}
              </div>

              <div class="area-chooser-row">
                <progress-bar
                  :percent="areaProgressPercent(area(areaProgress.id))"
                ></progress-bar>
                <div class="button-and-incomplete">
                  <button
                    @click="goToArea(areaProgress.id)"
                    class="btn primary-btn"
                  >
                    {{ areaProgressPercent(area(areaProgress.id)) === 0 ? $t("BEGIN") : areaProgressPercent(area(areaProgress.id)) === 100 ? $t("VIEW_AGAIN") : $t("CONTINUE") }}
                  </button>
                  <div
                    v-if="areaIncompleteCount(area(areaProgress.id)) > 0"
                    class="incomplete-count"
                  >
                    {{ areaIncompleteCount(area(areaProgress.id)) }}x
                    <exclamation-rectangle class="svg-icon" />
                  </div>
                </div>
              </div>
            </div>
            <div class="col-xs-4">
              <div class="area-chooser-image">
                <img
                  :src="area(areaProgress.id).image"
                  class="img img-responsive"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<!---------------------------------------------------------------------------->
<script>
import sortBy from "lodash/sortBy";
import map from "lodash/map";
import { sumBy } from "lodash/math";
// import {countBy} from "lodash/collection";

import { mapGetters } from "vuex";
import ProgressBar from "Components/common/ProgressBar";
import userMessages from "src/services/userMessages";
import CheckBadge from "Components/Svg/CheckBadge";
import Trophy from "Components/Svg/Trophy";
import Diamond from "Components/Svg/Diamond";
import Slash from "Components/Svg/Slash";
import ExclamationRectangle from "Components/Svg/ExclamationRectangle";

import log from "src/services/logger";
import { countBy } from "lodash/collection";

const logTag = "AreaChooser";

export default {
  components: {
    ExclamationRectangle,
    Slash,
    ProgressBar,
    CheckBadge,
    Trophy,
    Diamond,
  },

  data() {
    return {
      showUserProgress: config.features.showUserProgressInAreaChooser,
      showCategoryProgress: config.features.showCategoryProgress,
    };
  },

  computed: {
    ...mapGetters([
      "allProgress",
      "currentProgress",
      "currentAreaId",
      "area",
      "user",
      "categoriesProgress",
    ]),

    // areas are sorted by ID, descending, so newer areas are on top
    allAreasProgressSorted() {
      const allAreaProgressWithIds = map(
        this.allProgress,
        (areaProgress, areaId) => ({ ...areaProgress, id: areaId })
      );
      return sortBy(allAreaProgressWithIds, ["id"]).reverse();
    },

    allAreasScore() {
      return sumBy(this.allAreasProgressSorted, "score");
    },

    allAreasAchievementsCount() {
      return sumBy(this.allAreasProgressSorted, "achievements.achieved_count");
    },

    allCategoriesCount() {
      return Object.keys(this.categoriesProgress).length;
    },

    completeCategoriesCount() {
      return (
        countBy(this.categoriesProgress, function (categoryProgress) {
          return (
            categoryProgress.total_chapters ===
            categoryProgress.complete_chapters
          );
        })[true] ?? 0
      );
    },
  },

  methods: {
    goToArea(areaId) {
      // inform the server about preferred area, set current area, set area ID to headers
      // we don't need to reload
      this.$store
        .dispatch("changePreferredArea", areaId)
        .then(() => {
          this.$router.push(`/areas/${areaId}/categories`);
        })

        // changing of area failed, show the error
        .catch((reason) => {
          log.log(logTag, "Changing preferred area failed, reason follows: ");
          log.log(logTag, reason);
          this.$store.dispatch(
            "showErrorMessage",
            userMessages.afterChangePreferredArea(reason)
          );
        });
    },

    areaCompleteCategoriesCount(area) {
      const categoriesProgress = this.categoriesProgress;
      return sumBy(area.categories, function (category) {
        const progress = categoriesProgress[category.id];
        return progress.total_chapters === progress.complete_chapters ? 1 : 0;
      });
    },

    areaProgressPercent(area) {
      const categoriesProgress = this.categoriesProgress;
      const total = sumBy(area.categories, function (category) {
        return categoriesProgress[category.id].total_content_units;
      });
      const complete = sumBy(area.categories, function (category) {
        return categoriesProgress[category.id].complete_content_units;
      });
      return total === 0 ? 0 : (complete / total) * 100;
    },

    areaIncompleteCount(area) {
      return area.categories.length - this.areaCompleteCategoriesCount(area);
    },
  },
};
</script>
<!---------------------------------------------------------------------------->
<style lang="scss" type="text/scss"></style>
